/* Pouch zoom — sections/pouch-zoom.liquid
 *
 * A pinned scroll section: a full-bleed close-up on one refill pouch (the page's
 * fragrance) zooms out as the visitor scrolls to reveal a field of pouches.
 * Built and approved as a standalone prototype first (Apple's HomeView
 * "perfect face" reveal, re-done with real product cutouts).
 *
 * How it stays smooth (keep these when editing):
 *  - Every pouch is a bare <img> with its own transform, all driven by ONE
 *    shared camera. A layer whose only content is an image is composited
 *    straight from the decoded bitmap (Chrome "directly composited image",
 *    WebKit contents image), so the GPU scales the full-resolution photo and
 *    nothing re-rasterises mid-zoom. Scaling one big composite layer, or
 *    wrapper layers, went blurry or left holes (Chrome rasterises a
 *    will-change layer at the scale it had when promoted). No pseudo-elements
 *    on those images, and no contact shadows (removed 2026-10 at the owner's ask).
 *  - The zoom is interpolated in log space (S0 -> S1 as a ratio), so it feels
 *    even from 7x to 0.8x; progress is eased (sine) with a short hold at both
 *    ends, and followed with a time-based lag (~110 ms) so wheel ticks blur
 *    into one move.
 *  - Native position: sticky pin (the discovery-house pattern): progress comes
 *    from the track's rect, a rAF loop runs only while the section is on
 *    screen, will-change is on only within a viewport of it, and nothing is
 *    built (no image requests) until the section is two viewports away.
 *  - Reduced motion, no JS, or a missing config: the section falls back to the
 *    static grid the Liquid renders (.pouch-zoom--static).
 */
(function () {
  'use strict';
  if (window.PouchZoom) return;

  /* Geometry of a set's canvas (see scripts/pouch_zoom/README.md); the section passes it in the config, these are the pouch values. */
  var DEFAULT_GEO = { aspect: 1960 / 1600, wFrac: 0.73, hFrac: 0.89, logoY: -0.26, logoW: 0.45, logoH: 0.18, gap: 0.08,
                      grid: { cols: 7, rows: 5, cellW: 300 }, gridMobile: { cols: 5, rows: 7, cellW: 150 } };

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function smooth(a, b, x) { var t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); }
  function easeSine(x) { return -(Math.cos(Math.PI * x) - 1) / 2; }
  function isMobile() { return window.innerWidth < 750; }

  function PouchZoom(root) {
    this.root = root;
    this.track = root.querySelector('.pouch-zoom__track');
    this.pin = root.querySelector('.pouch-zoom__pin');
    this.stage = root.querySelector('.pouch-zoom__stage');
    this.copy = root.querySelector('.pouch-zoom__copy');
    this.cta = root.querySelector('.pouch-zoom__cta');
    var cfgEl = root.querySelector('[data-pouch-zoom-config]');
    try { this.cfg = cfgEl ? JSON.parse(cfgEl.textContent) : null; } catch (e) { this.cfg = null; }
    this.geo = {}; for (var k in DEFAULT_GEO) this.geo[k] = (this.cfg && this.cfg.geo && this.cfg.geo[k] != null) ? this.cfg.geo[k] : DEFAULT_GEO[k];
    this.cells = []; this.heroIndex = 0; this.listeners = []; this.raf = null; this.inView = false;
    this.target = 0; this.cur = 0; this.lastApplied = -1; this.lastT = 0; this.built = false;
    this.cam = { S0: 1, S1: 1, fy: 0, vw: 0, vh: 0 };

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.track || !this.pin || !this.stage || !this.cfg || !this.cfg.pouches || !this.cfg.pouches.length || reduce) {
      root.classList.add('pouch-zoom--static');
      return;
    }
    root.classList.add('pouch-zoom--js');
    // The visually-hidden list keeps the pouch names for screen readers; its images only matter in static mode.
    Array.prototype.forEach.call(root.querySelectorAll('.pouch-zoom__list img'), function (img) { img.parentNode.removeChild(img); });

    // Build two viewports early so the images are decoded before the section is reached; nothing is requested for
    // visitors who never scroll this far.
    var self = this;
    if ('IntersectionObserver' in window) {
      this.armIO = new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        obs.disconnect(); self.armIO = null; self.init();
      }, { rootMargin: '200% 0px' });
      this.armIO.observe(root);
    } else { this.init(); }
  }

  PouchZoom.prototype.on = function (target, type, fn, opts) { target.addEventListener(type, fn, opts); this.listeners.push([target, type, fn, opts]); };

  /* The pin sits under the sticky header; measure it so the stage is exactly the visible space (same as the house). */
  PouchZoom.prototype.measureChrome = function () {
    var header = document.querySelector('.section-header-eb');
    if (!header || !/sticky|fixed/.test(getComputedStyle(header).position)) {
      header = Array.prototype.filter.call(document.querySelectorAll('.shopify-section-group-header-group'), function (el) {
        return /sticky|fixed/.test(getComputedStyle(el).position);
      })[0] || null;
    }
    var top = header ? Math.round(header.getBoundingClientRect().height) : 0;
    this.root.style.setProperty('--pz-header', top + 'px');
  };

  PouchZoom.prototype.gridSpec = function () { return isMobile() ? this.geo.gridMobile : this.geo.grid; };

  /* Lay the field out once: the hero pouch in the centre cell, the rest around it in block order, repeating. */
  PouchZoom.prototype.build = function () {
    var g = this.gridSpec(), P = this.cfg.pouches, n = P.length;
    this.cols = g.cols; this.rows = g.rows; this.cellW = g.cellW;
    this.cellH = Math.round(g.cellW * this.geo.aspect); this.gap = Math.round(g.cellW * this.geo.gap);
    this.fieldW = this.cols * this.cellW + (this.cols - 1) * this.gap;
    this.fieldH = this.rows * this.cellH + (this.rows - 1) * this.gap;
    var centre = Math.floor(this.rows / 2) * this.cols + Math.floor(this.cols / 2);
    var heroIdx = clamp(this.cfg.hero | 0, 0, n - 1);
    this.heroIndex = centre;
    this.heroPouch = P[heroIdx];
    var small = isMobile(), frag = document.createDocumentFragment();
    this.stage.innerHTML = ''; this.cells = [];
    for (var k = 0; k < this.cols * this.rows; k++) {
      var p = P[((k - centre + heroIdx) % n + n) % n];
      var isHero = k === centre;
      var w = isHero ? (p.src['3200'] ? 3200 : 1600) : (small ? 400 : 800);   // the hero gets the biggest cut there is
      var src = p.src[String(w)] || p.src['800'];
      var img = document.createElement('img');
      img.className = 'pouch-zoom__cell'; img.style.setProperty('--w', w + 'px');
      img.width = w; img.height = Math.round(w * this.geo.aspect);
      img.alt = isHero ? (p.name || '') : ''; img.decoding = 'async'; img.draggable = false;
      img.src = src;
      if (img.decode) img.decode().catch(function () {});   // decode off the main thread now, not on first paint
      frag.appendChild(img);
      this.cells.push({ el: img, w: w, col: k % this.cols, row: Math.floor(k / this.cols) });
    }
    this.stage.appendChild(frag);
    this.built = true;
  };

  PouchZoom.prototype.layout = function () {
    this.measureChrome();
    var vw = this.pin.clientWidth, vh = this.pin.clientHeight, cam = this.cam;
    cam.vw = vw; cam.vh = vh;
    // The camera opens on a plain patch of the hero's colour above the wordmark, so the copy reads over it:
    // logoY = its centre below the cell centre (negative = above), logoW / logoH = its size, all as fractions of the cell.
    var h = this.heroPouch || {}, g = this.geo;
    var logoY = h.logoY != null ? h.logoY : g.logoY, logoW = h.logoW != null ? h.logoW : g.logoW, logoH = h.logoH != null ? h.logoH : g.logoH;
    // start: zoomed until the plain patch fills the screen both ways, so nothing printed shows
    cam.S0 = Math.max(vw / (logoW * this.cellW), vh / (logoH * this.cellH));
    // end: the field runs past the edges
    cam.S1 = (1.22 * vw) / this.fieldW;
    cam.fy = logoY;
    this.apply(this.cur, true);
  };

  PouchZoom.prototype.progress = function () {
    var stickyTop = parseFloat(getComputedStyle(this.pin).top) || 0;
    var r = this.track.getBoundingClientRect();
    var span = this.track.offsetHeight - this.pin.offsetHeight;
    return span > 0 ? clamp((stickyTop - r.top) / span, 0, 1) : 1;
  };

  PouchZoom.prototype.apply = function (p, force) {
    if (!this.built) return;
    if (!force && Math.abs(p - this.lastApplied) < 0.00015) return;
    this.lastApplied = p;
    var cam = this.cam, cells = this.cells;
    // a short hold at the start so the first wheel tick doesn't move the close-up, and a settle at the end
    var e = easeSine(smooth(0.04, 0.94, p));
    var s = cam.S0 * Math.pow(cam.S1 / cam.S0, e);
    // the opening patch stays at the centre of the screen at every scale, so the field opens out around it
    var cx = cam.vw / 2, cy = cam.vh / 2 - s * cam.fy * this.cellH;
    var hc = cells[this.heroIndex] || cells[0], cellW = this.cellW, cellH = this.cellH, gap = this.gap;
    for (var i = 0; i < cells.length; i++) {
      var c = cells[i];
      var dx = (c.col - hc.col) * (cellW + gap), dy = (c.row - hc.row) * (cellH + gap);
      var sigma = s * cellW / c.w;                      // image scale: natural width -> cell width on screen
      var ch = c.w * this.geo.aspect;
      var x = cx + s * dx - sigma * c.w / 2, y = cy + s * dy - sigma * ch / 2;
      c.el.style.transform = 'translate(' + x.toFixed(2) + 'px,' + y.toFixed(2) + 'px) scale(' + sigma.toFixed(4) + ')';
    }
    var fade = 1 - smooth(0.02, 0.2, p);
    this.copy.style.opacity = fade.toFixed(3);
    this.copy.style.transform = 'translateY(' + (-(1 - fade) * 36).toFixed(1) + 'px)';
    var on = p >= 0.9;   // the button just appears as the field settles, no fade
    this.cta.style.opacity = on ? '1' : '0';
    this.cta.classList.toggle('is-on', on);
  };

  PouchZoom.prototype.frame = function (now) {
    this.raf = null;
    var dt = this.lastT ? Math.min(64, now - this.lastT) : 16; this.lastT = now;
    var k = 1 - Math.exp(-dt / 110);        // time-based follow, ~110 ms lag, independent of frame rate
    this.cur += (this.target - this.cur) * k;
    if (Math.abs(this.target - this.cur) < 0.0002) this.cur = this.target;
    this.apply(this.cur);
    if (this.cur !== this.target) this.tick(); else this.lastT = 0;
  };
  PouchZoom.prototype.tick = function () {
    var self = this;
    if (this.raf == null) this.raf = requestAnimationFrame(function (t) { self.frame(t); });
  };
  PouchZoom.prototype.onScroll = function () {
    if (!this.inView) return;
    this.target = this.progress();
    this.tick();
  };

  PouchZoom.prototype.init = function () {
    if (this.built) return;
    var self = this;
    this.build();
    this.layout();
    this.cur = this.target = this.progress(); this.apply(this.cur, true);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.measureChrome(); });
    this.io = new IntersectionObserver(function (entries) {
      self.inView = entries.some(function (en) { return en.isIntersecting; });
      self.root.classList.toggle('is-live', self.inView);
      if (self.inView) { self.target = self.progress(); self.tick(); }
    }, { rootMargin: '100% 0px' });
    this.io.observe(this.root);
    this.on(window, 'scroll', function () { self.onScroll(); }, { passive: true });
    var t;
    var onResize = function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var size = [self.pin.offsetWidth, self.pin.offsetHeight];
        if (self.layoutSize && self.layoutSize[0] === size[0] && self.layoutSize[1] === size[1]) return;   // iOS address-bar noise
        self.layoutSize = size;
        self.build(); self.layout(); self.cur = self.target = self.progress(); self.apply(self.cur, true);
      }, 150);
    };
    this.layoutSize = [this.pin.offsetWidth, this.pin.offsetHeight];
    this.on(window, 'resize', onResize);
    this.on(window, 'orientationchange', onResize);
  };

  PouchZoom.prototype.destroy = function () {
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    this.listeners = [];
    if (this.io) this.io.disconnect();
    if (this.armIO) this.armIO.disconnect();
    if (this.raf != null) cancelAnimationFrame(this.raf);
    this.root.pouchZoom = null;
  };

  function mount(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-pouch-zoom]'), function (root) {
      if (root.pouchZoom) return;
      root.pouchZoom = new PouchZoom(root);
    });
  }
  window.PouchZoom = PouchZoom;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { mount(); });
  else mount();
  // Theme editor: re-mount when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) { mount(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    Array.prototype.forEach.call(e.target.querySelectorAll('[data-pouch-zoom]'), function (root) { if (root.pouchZoom) root.pouchZoom.destroy(); });
  });
})();
